import React, { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../api/client';
import { providerEventsApi, ProviderEvent } from '../api/providerEvents';

interface Props {
  providerId: string;
}

const terminalStatuses = new Set(['PROCESSED', 'DUPLICATE', 'INVALID_SIGNATURE']);

export default function ProviderEventsPanel({ providerId }: Props) {
  const [events, setEvents] = useState<ProviderEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [eventType, setEventType] = useState('');
  const [payload, setPayload] = useState<string | null>(null);
  const [escalatedCount, setEscalatedCount] = useState(0);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [loadedEvents, counts] = await Promise.all([
        providerEventsApi.list({
          providerId,
          status: status || undefined,
          eventType: eventType || undefined,
          limit: 200
        }),
        providerEventsApi.escalatedCounts()
      ]);
      setEvents(loadedEvents);
      setEscalatedCount(counts.find((item) => item.providerId === providerId)?.count ?? 0);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Could not load provider events.'));
    } finally {
      setLoading(false);
    }
  }, [eventType, providerId, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const showPayload = async (id: string) => {
    try {
      setPayload(await providerEventsApi.getPayload(id));
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Could not load event payload.'));
    }
  };

  const retry = async (id: string) => {
    try {
      await providerEventsApi.retry(id);
      await load();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Could not requeue event.'));
    }
  };

  const ignore = async (id: string) => {
    const reason = window.prompt('Reason for ignoring this event') ?? undefined;
    if (reason === undefined) return;
    try {
      await providerEventsApi.ignore(id, reason);
      await load();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Could not ignore event.'));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">Status</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)} className="border border-gray-300 rounded-lg px-3 py-2">
            <option value="">All</option>
            {['RECEIVED', 'PROCESSING', 'PROCESSED', 'DUPLICATE', 'FAILED', 'ESCALATED', 'IGNORED', 'INVALID_SIGNATURE'].map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">Type</span>
          <input value={eventType} onChange={(event) => setEventType(event.target.value.toUpperCase())} placeholder="PAYMENT" className="border border-gray-300 rounded-lg px-3 py-2 w-36" />
        </label>
        <button type="button" onClick={() => void load()} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-teal-600 hover:bg-teal-700">
          Refresh
        </button>
        {escalatedCount > 0 && (
          <button
            type="button"
            onClick={() => setStatus('ESCALATED')}
            className="px-4 py-2 rounded-lg text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100"
          >
            Escalated: {escalatedCount}
          </button>
        )}
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}
      {loading ? (
        <p className="text-sm text-gray-500">Loading events…</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-gray-500">No provider events match the filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Received</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Provider status</th>
                <th className="px-3 py-2 text-left">Processing</th>
                <th className="px-3 py-2 text-left">Signature</th>
                <th className="px-3 py-2 text-left">Attempts</th>
                <th className="px-3 py-2 text-left">Error</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((item) => (
                <tr key={item.id} className="border-t border-gray-100">
                  <td className="px-3 py-2 whitespace-nowrap">{new Date(item.receivedAt).toLocaleString()}</td>
                  <td className="px-3 py-2 font-mono">{item.eventType}</td>
                  <td className="px-3 py-2 font-mono">{item.providerStatus || '—'}</td>
                  <td className="px-3 py-2 font-mono">{item.processingStatus}</td>
                  <td className="px-3 py-2">{item.signatureValid ? 'Valid' : 'Invalid'}</td>
                  <td className="px-3 py-2">{item.processingAttempts}</td>
                  <td className="px-3 py-2 max-w-xs truncate" title={item.processingError || ''}>{item.processingError || '—'}</td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button type="button" onClick={() => void showPayload(item.id)} className="text-teal-700 hover:underline">Payload</button>
                    {!terminalStatuses.has(item.processingStatus) && (
                      <button type="button" onClick={() => void ignore(item.id)} className="text-amber-700 hover:underline">Ignore</button>
                    )}
                    {['FAILED', 'IGNORED', 'ESCALATED'].includes(item.processingStatus) && (
                      <button type="button" onClick={() => void retry(item.id)} className="text-indigo-700 hover:underline">Retry</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {payload !== null && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h3 className="font-semibold">Raw provider payload</h3>
              <button type="button" onClick={() => setPayload(null)} className="text-gray-500 hover:text-gray-800">Close</button>
            </div>
            <pre className="overflow-auto p-5 text-xs bg-slate-950 text-slate-100">{payload}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
